import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { FormProvider } from 'react-hook-form';
import {
  AsyncSelect,
  Checkbox,
  FormCell,
  FormLayout,
  FormRow,
  FormSection,
  RichTextArea,
  Select,
  TextInput,
} from '~/components';
import { loadCategoryOptions } from '~/features/vault/profile/api';
import { useCustomForm } from '~/hooks/useCustomForm';
import { SubspecialtyFields } from '../../../components/SubspecialtyFields';
import { useSetProviderCategory } from '../../../hooks/useSetProviderCategory';
import { useResetSubspecialties } from '../../../hooks/useSubspecialties';
import { useMsaasListingsActorRef } from '../../msaasProvider';
import type { Context as MsaasListingContext } from '../../types';
import { emptyBasicInfoForm, WORK_LOCATION_OPTIONS } from './constants';
import { BasicInfoFormSchema, BasicInfoFormValues } from './types';
import { loadAOIOptionByProviderCategory, transformApiToBasicInfoForm } from './utils';

const TAGS = CT.employer.msaasListings.basicInfoForm;

export const BasicInfoForm = ({ footer }: { footer: React.ReactNode }) => {
  const msaasListingsRef = useMsaasListingsActorRef();
  const {
    basicInfoRef,
    modal,
    listing: msaasListing,
    listingSide,
  } = useSelector(msaasListingsRef, (s): MsaasListingContext => {
    if (typeof s === 'object' && s !== null && 'context' in s) {
      return s.context as MsaasListingContext;
    }
    throw new Error('Invalid state: context not found');
  });

  const methods = useCustomForm<BasicInfoFormValues>({
    resolver: zodResolver(BasicInfoFormSchema),
    // [MCV-1889] A new listing carries the side picked in the "+ New" menu (undefined when the
    // flag is off, which leaves the request exactly as it was).
    defaultValues: msaasListing
      ? transformApiToBasicInfoForm(msaasListing)
      : { ...emptyBasicInfoForm, listingSide },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty },
  } = methods;

  const onSubmitSuccess = (values: BasicInfoFormValues) => {
    if (msaasListing?.id && isDirty) {
      basicInfoRef.send({ type: 'ON_UPDATE', payload: { values } });
    } else if (!modal.edit && !msaasListing?.id) {
      basicInfoRef.send({ type: 'ON_ADD', payload: { values } });
    } else {
      msaasListingsRef.send({ type: 'ON_NEXT' });
    }
  };

  const onSubmitError = (err: unknown) => {
    console.log('Basic Info Form Submit Error: ', err, methods.getValues());
  };

  useSetProviderCategory(msaasListing, (providerCategory) =>
    setValue('providerCategory', providerCategory)
  );

  useResetSubspecialties(methods, {
    resetGeneralFlag: true,
    clearSpecialtyWithCategory: { empty: { label: '', value: '' } },
  });

  const providerCategory = watch('providerCategory');
  const specialtyId = watch('aoiSpecialty')?.value;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          <FormSection componentsPerLine={1}>
            <FormRow>
              <TextInput
                name={'listingTitle'}
                placeholder="e.g. Emergency Medicine Physician - Night Shift Lead"
                cyTag={TAGS.listingTitle}
                label={'Listing Title'}
                required
              />
            </FormRow>
            <FormRow>
              {/* [MCV-1586] The field accepts up to 8192 characters but rendered
                at the browser's default two rows, so composing or re-reading a
                real description meant scrolling a two-line window. `rows` is
                what sizes it -- createFormFieldStyles sets no height, so there
                is nothing here for a CSS height to fight with. */}
              <RichTextArea
                name={'listingDescription'}
                cyTag={TAGS.listingDescription}
                label={'Listing Description'}
              />
            </FormRow>
          </FormSection>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <AsyncSelect
                name={'providerCategory'}
                cyTag={TAGS.providerCategory}
                label={'Provider Category'}
                minChars={false}
                loadOptions={loadCategoryOptions}
                required
              />
            </FormRow>
            <FormRow>
              <AsyncSelect
                name={'aoiSpecialty'}
                key={providerCategory?.value}
                cyTag={TAGS.aoiSpecialty}
                label={'Provider Specialty'}
                minChars={false}
                required
                placeholder={
                  providerCategory?.label ? 'Select...' : 'Select A Provider Category First'
                }
                loadOptions={(inputValue: string) =>
                  loadAOIOptionByProviderCategory(providerCategory.value, inputValue)
                }
              />
            </FormRow>
            {specialtyId && (
              <FormCell span={12}>
                <SubspecialtyFields
                  specialtyId={specialtyId}
                  subspecialtiesCyTag={TAGS.subspecialties}
                  generalSpecialtyCyTag={TAGS.generalSpecialtyFlag}
                />
              </FormCell>
            )}
          </FormSection>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <Select
                name={'workLocation'}
                cyTag={TAGS.workLocation}
                label={'Work Location'}
                options={WORK_LOCATION_OPTIONS}
              />
            </FormRow>
          </FormSection>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <Checkbox
                name={'isRenewable'}
                cyTag={TAGS.isRenewable}
                label={'Is Contract Renewable?'}
              />
            </FormRow>
          </FormSection>
          {footer}
        </FormLayout>
      </form>
    </FormProvider>
  );
};
